import React from 'react'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faBookmark as farBookmark } from '@fortawesome/pro-regular-svg-icons'
import { faBookmark as fasBookmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@scoir/tooltips'
import cn from 'classnames'
import { get } from 'lodash'
import { connect } from 'react-redux'

import { useMetadataToTrackedEvent } from 'common/metadata-custom-hooks'
import { useIsAuthed } from 'components/AuthShield'
import CollegeSwitchSurvey from 'components/CollegeSwitchSurvey'
import { FEATURE_NAMES } from 'components/ConfiguredDynamicFeatureProvider/features'
import withDynamicFeature from 'components/DynamicFeatureProvider/WithDynamicFeature'
import ShareInterestContext from 'components/ShareInterest/ShareInterestProvider/Context'
import { actions as prospectActions } from 'store/prospects'
import { CollegeDetail, Prospect } from 'store/prospects/type-definitions'
import { actions as snactions } from 'store/snackbar'
import { useShareInterest } from 'store/user/hooks'

import { useFollowToggle, useFollowToggleText, useIsApplyingOrApplied, useIsFollowingState } from './service'

const useStyles = makeStyles({
  addToMyCollegesComp: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  addToMyCollegesButtonIcon: {
    height: '18px',
    width: '18px',
    margin: 0,
  },
})

type AddToMyCollegesActionProps = {
  buttonProps?: any
  className?: string
  disabled?: boolean
  displayText?: boolean
  hideIcon?: boolean
  ignoreSurvey?: boolean
  name: string
  onAddSnack: (message: string) => void
  onlyShowFollowable?: boolean
  onToggle: (scid: number) => void
  afterToggle?: () => void
  prospects?: {
    following?: Prospect[]
    applying?: Prospect[]
    applied?: Prospect[]
    notInterested?: Prospect[]
  }
  scid: number
}

export const AddToMyCollegesAction = ({
  buttonProps,
  className,
  disabled = false,
  displayText = true,
  hideIcon,
  ignoreSurvey = false,
  name,
  onAddSnack,
  onlyShowFollowable,
  onToggle,
  afterToggle,
  prospects,
  scid,
}: AddToMyCollegesActionProps) => {
  const classes = useStyles()
  const isAuthed = useIsAuthed()
  const [showSurvey, setShowSurvey] = React.useState(false)
  const [type, setType] = React.useState<'follow' | 'unfollow'>()
  const [saveFollowState, setSaveFollowState] = React.useState(false)
  const isApplyingOrApplied = useIsApplyingOrApplied(scid, prospects, true)
  const labelText = useFollowToggleText(scid, prospects)
  const toggleState = useIsFollowingState(scid, prospects)
  const isDisabled = disabled || isApplyingOrApplied
  const followToggle = useFollowToggle({
    scid,
    prospects,
    onToggle: () => onToggle(scid),
    onAddSnack,
    setSaveFollowState,
  })
  const eventDataRetriever = useMetadataToTrackedEvent()
  const referrer = eventDataRetriever({ flat: true }).EventSourceReferrer
  const [isLoading, setIsLoading] = React.useState(false)

  const showShareInterest = useShareInterest(scid) && !toggleState
  const { selectCollege } = React.useContext(ShareInterestContext)

  const faIcon = toggleState || saveFollowState ? fasBookmark : farBookmark
  const icon = hideIcon ? undefined : (
    <FontAwesomeIcon
      className={classes.addToMyCollegesButtonIcon}
      icon={faIcon as IconProp}
    />
  )

  const props = {
    variant: toggleState || saveFollowState ? 'outlined' : 'contained',
    disabled: isDisabled,
    ...buttonProps,
  }

  const handleOnClick = async () => {
    // NOTE: Introduced with Showed Interest Flow - no longer ignoring survey on follow
    // If we're unauthed OR ignoring, just toggle and don't show survey
    if (!isAuthed || ignoreSurvey) {
      setIsLoading(true)
      showShareInterest && selectCollege({ Name: name, Scid: scid } as CollegeDetail, referrer)
      await followToggle()
      setIsLoading(false)
      afterToggle && afterToggle()
      return
    }
    setType(toggleState ? 'unfollow' : 'follow')
    // Show survey only on unfollow or we're not showing the shared interest flow
    setShowSurvey(toggleState || !showShareInterest)
    // Temporarily hold follow state
    if (!toggleState) {
      setSaveFollowState(true)
    }
    // Shared interest flow takes care of showing the survey after follow
    showShareInterest && selectCollege({ Name: name, Scid: scid } as CollegeDetail, referrer, handleOnClose)
  }
  const handleOnSubmit = async () => {
    // Submit unfollow if survey action is unfollow
    if (toggleState) {
      setIsLoading(true)
      await followToggle()
      setIsLoading(false)
      setSaveFollowState(false)
    }
  }
  const handleOnClose = async () => {
    setShowSurvey(false)
    // Submit follow no matter what survey action is
    if (!toggleState) {
      setIsLoading(true)
      await followToggle()
      setIsLoading(false)
    }
  }

  if (onlyShowFollowable && labelText !== 'Add to My Colleges') {
    return null
  }

  const tooltipId = `follow-tooltip-${scid}`

  return (
    <>
      <Tooltip
        title={labelText}
        id={tooltipId}
      >
        <div className={cn(classes.addToMyCollegesComp, className)}>
          {displayText ? (
            <Button
              {...props}
              aria-describedby={tooltipId}
              onClick={handleOnClick}
              color="primary"
              startIcon={icon}
              disabled={isLoading}
            >
              Follow{toggleState || saveFollowState ? 'ing' : null}
            </Button>
          ) : (
            <IconButton
              {...props}
              aria-label={`Follow${toggleState || saveFollowState ? 'ing' : null}`}
              aria-describedby={tooltipId}
              onClick={handleOnClick}
              color="primary"
              disabled={isLoading}
            >
              {icon}
            </IconButton>
          )}
        </div>
      </Tooltip>
      {showSurvey && type && (
        <CollegeSwitchSurvey
          type={type}
          referrer={referrer}
          show={showSurvey}
          prospect={{ CollegeName: name, CollegeScid: scid }}
          onClose={handleOnClose}
          onSubmit={handleOnSubmit}
        />
      )}
    </>
  )
}

const WDF_AddToMyCollegesAction = withDynamicFeature(AddToMyCollegesAction, { name: FEATURE_NAMES.MyColleges })

export default connect(
  (state: any) => ({
    prospects: get(state, 'prospects.groups'),
  }),
  {
    onToggle: prospectActions.fetchProspects,
    onAddSnack: snactions.addSnackbarItem,
  },
)(WDF_AddToMyCollegesAction)
